import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { WorkerDviDocument } from './workerClient';
import { TwistExplorer } from '../widgets/TwistExplorer';

/** interactive 環境のウィジェット登録表 (\interactive{名前} の名前 → コンポーネント) */
const WIDGETS: Record<string, React.ComponentType> = {
  'twist-explorer': TwistExplorer,
};

/** 前景色 #333 (global.css の --color-fg と揃える) */
const FG = 0x333333ff;
const SP_PER_PT = 65536;
/** ページ個別クロップの余白 (pt)。アンチエイリアスの縁を保護する */
const CROP_MARGIN_PT = 1;

/** 草稿指定 (draft prop) の前景色。プロースの赤字 (CSS) と揃える */
const DRAFT_FG = 0xb00020ff;

type Seg =
  | { t: 'text'; s: string }
  | { t: 'br' }
  /** sl/sr = \xkanjiskip 相当の余白 (隣接文字からビルド時に決定。F2) */
  | { t: 'inline'; p: number; sl?: boolean; sr?: boolean }
  | { t: 'display'; p: number }
  | { t: 'link'; href: string; s: string }
  /** 文献項目の開始 (\bib)。[ラベル] を id="bib-key" のアンカーとして組む */
  | { t: 'bib'; key: string; label: string }
  /** 文献引用 (\cite)。ラベルはビルドの解決パスで references の \bib から埋まる */
  | { t: 'cite'; key: string; label: string | null; detail: string | null };

interface FragJson {
  env:
    | 'proposition'
    | 'lemma'
    | 'theorem'
    | 'references'
    | 'bibguide'
    | 'proof'
    | 'notation'
    | 'definition'
    | 'construction'
    | 'example'
    | 'overview'
    | 'remark'
    | 'body'
    | 'section'
    | 'subsection'
    | 'figure'
    | 'interactive';
  label: string | null;
  body: Seg[];
  qedPage: number | null;
  /** ビルド時計測のページ寸法 (sp)。w/h/d = 幅/高さ/ベースライン深さ */
  pages?: { w: number; h: number; d: number }[];
}

interface PageMetrics {
  widthSp: number;
  heightSp: number;
  depthSp: number;
}

const ENV_TITLES: Record<FragJson['env'], string> = {
  proposition: '命題',
  lemma: '補題',
  theorem: '定理',
  references: '参考文献',
  bibguide: '文献案内',
  proof: '証明',
  notation: '記法',
  definition: '定義',
  construction: '構成',
  example: '例',
  overview: '概要',
  remark: '補足',
  body: '',
  section: '',
  subsection: '',
  figure: '',
  interactive: '',
};

/**
 * 文献引用 [ラベル(, 注記)]。クリックで同ページの文献項目 (#bib-key) へ移動し、
 * ホバー/フォーカスで項目の組版済み内容をツールチップ表示する。
 * ツールチップは描画済みの項目 (.frag-para) を DOM ごと複製する — 数式 canvas は
 * cloneNode でビットマップが失われるため drawImage で描き写す。
 */
function Cite({ seg }: { seg: { key: string; label: string | null; detail: string | null } }) {
  const [tip, setTip] = useState(false);
  const tipRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = tipRef.current;
    if (!tip || !el) {
      return;
    }
    const para = document.getElementById(`bib-${seg.key}`)?.closest('.frag-para');
    if (!para) {
      return;
    }
    const clone = para.cloneNode(true) as HTMLElement;
    // id の重複を避ける (複製はスクロール先にしない)
    clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
    const origs = para.querySelectorAll('canvas');
    clone.querySelectorAll('canvas').forEach((c, i) => {
      const orig = origs[i];
      if (orig) {
        c.getContext('2d')?.drawImage(orig, 0, 0);
      }
    });
    el.replaceChildren(clone);
    return () => el.replaceChildren();
  }, [tip, seg.key]);

  const text = `[${seg.label ?? seg.key}${seg.detail ? `, ${seg.detail}` : ''}]`;
  return (
    <span
      className="frag-cite-wrap"
      onMouseEnter={() => setTip(true)}
      onMouseLeave={() => setTip(false)}
    >
      <a
        className="frag-cite"
        href={`#bib-${seg.key}`}
        onFocus={() => setTip(true)}
        onBlur={() => setTip(false)}
      >
        {text}
      </a>
      {tip && <span className="frag-cite-tip" ref={tipRef} />}
    </span>
  );
}

function useDevicePixelRatio(): number {
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1);
  useEffect(() => {
    const mq = window.matchMedia(`(resolution: ${dpr}dppx)`);
    const onChange = () => setDpr(window.devicePixelRatio || 1);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [dpr]);
  return dpr;
}

/**
 * 数式単位分割の断片表示 (spec/03 Q10)。
 *
 * 散文は HTML としてコンテナ幅で行分割し、数式 (frag.json が参照する
 * formulas DVI の各ページ) だけを DVI ラスタで合成する。
 * - インライン数式: ベースライン深さで vertical-align を合わせた
 *   inline-block canvas
 * - ディスプレイ数式: 中央寄せのブロック
 * - proof の QED (□): 右寄せ
 * 数式内部は 1sp 再現のまま、行分割・右端・中央はコンテナに追従する。
 */
export function FragmentView({ src, draft = false }: { src: string; draft?: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<WorkerDviDocument | null>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [degraded, setDegraded] = useState(false);
  const [frag, setFrag] = useState<FragJson | null>(null);
  const [metrics, setMetrics] = useState<PageMetrics[] | null>(null);
  const [facesReady, setFacesReady] = useState(false);
  const dpr = useDevicePixelRatio();

  // ロード: frag.json → formulas DVI → メトリクス (寸法確定) → フォント本体。
  // frag.json の失敗のみ致命 (断片全体をエラー表示)。数式リソース側の失敗は
  // 散文を残したまま数式だけ未描画に degrade する (F1)。
  useEffect(() => {
    let cancelled = false;
    setFrag(null);
    setMetrics(null);
    setFacesReady(false);
    setError(null);
    setDegraded(false);
    (async () => {
      const res = await fetch(`${src}.frag.json`);
      if (!res.ok) {
        throw new Error(`${src}.frag.json: HTTP ${res.status}`);
      }
      // frag.json 未生成のとき dev サーバは SPA の index.html を 200 で返す。
      // それを JSON.parse すると生の SyntaxError になるので、先に content-type で
      // 検知して分かりやすく示す (組版待ち: build-tex 完了後に再読み込み)。
      if (!(res.headers.get('content-type') ?? '').includes('json')) {
        throw new Error('プレビュー未生成(組版待ち)。build-tex 完了後に再読み込みしてください。');
      }
      const fragJson = (await res.json()) as FragJson;
      if (cancelled) {
        return;
      }
      // ビルド時計測の寸法が入っていれば、DVI やフォントの取得を待たずに
      // この時点で最終レイアウトが確定する (以降レイアウトは動かない)
      const prebuilt = fragJson.pages?.map((p) => ({
        widthSp: p.w,
        heightSp: p.h,
        depthSp: p.d,
      }));
      if (prebuilt && prebuilt.length > 0) {
        setMetrics(prebuilt);
      }
      setFrag(fragJson);
      const pages =
        fragJson.body.filter((s) => s.t === 'inline' || s.t === 'display').length +
        (fragJson.qedPage !== null ? 1 : 0);
      if (pages === 0) {
        return;
      }
      try {
        const doc = await WorkerDviDocument.open(`${src}.formulas.dvi`);
        if (cancelled) {
          doc.close();
          return;
        }
        docRef.current = doc;
        await doc.resolveFonts('metrics');
        if (cancelled) {
          return;
        }
        if (!prebuilt || prebuilt.length === 0) {
          // 事前計測が無い場合のフォールバック (メトリクス取得後に実測)
          const all: PageMetrics[] = [];
          for (let p = 0; p < doc.pageCount; p++) {
            all.push(await doc.pageMetricsSp(p, 'page-crop', CROP_MARGIN_PT));
          }
          if (cancelled) {
            return;
          }
          setMetrics(all);
        }
        await doc.resolveFonts('face');
        if (!cancelled) {
          setFacesReady(true);
        }
      } catch (e) {
        // 数式リソースの失敗: 散文はそのまま、数式だけ未描画で残す
        console.warn(`formulas unavailable for ${src}:`, e);
        if (!cancelled) {
          setDegraded(true);
        }
      }
    })().catch((e: unknown) => {
      if (!cancelled) {
        setError(String(e));
      }
    });
    return () => {
      cancelled = true;
      docRef.current?.close();
      docRef.current = null;
    };
  }, [src]);

  // 描画: フォント確定後に全ページを実効解像度でラスタライズする
  useEffect(() => {
    const doc = docRef.current;
    if (!doc || !facesReady || !metrics) {
      return;
    }
    const fontSizePx = rootRef.current
      ? parseFloat(getComputedStyle(rootRef.current).fontSize) || 16
      : 16;
    const devicePxPerPt = (fontSizePx / 10) * dpr;
    // 草稿指定の断片は数式も赤で描く (プロースの赤字と揃える)
    const fg = draft ? DRAFT_FG : FG;
    const renderKey = `${src}:${devicePxPerPt.toFixed(4)}:${fg}`;
    // ラスタライズは Worker (C3)。await 中の再入は dataset.pending で防ぐ
    canvasRefs.current.forEach((canvas, page) => {
      if (
        !canvas ||
        canvas.dataset.rendered === renderKey ||
        canvas.dataset.pending === renderKey
      ) {
        return;
      }
      canvas.dataset.pending = renderKey;
      doc
        .renderPage(page, devicePxPerPt, 'page-crop', CROP_MARGIN_PT, fg)
        .then((image) => {
          if (canvas.dataset.pending !== renderKey) {
            return;
          }
          delete canvas.dataset.pending;
          canvas.width = image.width;
          canvas.height = image.height;
          canvas.getContext('2d')?.putImageData(image, 0, 0);
          canvas.classList.remove('dvi-skeleton');
          canvas.dataset.rendered = renderKey;
        })
        .catch((e: unknown) => {
          delete canvas.dataset.pending;
          console.warn(`render failed for ${src} page ${page}:`, e);
          setDegraded(true);
        });
    });
  }, [src, frag, metrics, facesReady, dpr, draft]);

  if (error) {
    return <p className="dvi-status dvi-error">表示に失敗しました: {error}</p>;
  }
  if (!frag) {
    return <div className="frag" ref={rootRef} />;
  }

  // CSS 寸法は em で指定する (10pt = 1em なので 1sp = 1/(10·65536) em)
  const emPerSp = 1 / (10 * SP_PER_PT);

  const formulaCanvas = (
    page: number,
    kind: 'inline' | 'display' | 'qed',
    spaces?: { sl?: boolean; sr?: boolean },
  ) => {
    const m = metrics?.[page];
    // \xkanjiskip 相当 2.4pt = 0.24em。クロップ余白 0.1em を差し引いた
    // 0.14em を、ビルド時に決めた側にだけ与える (F2)
    const XSP = '0.14em';
    const style: React.CSSProperties = m
      ? {
          width: `${m.widthSp * emPerSp}em`,
          height: `${m.heightSp * emPerSp}em`,
          verticalAlign: kind === 'inline' ? `${-m.depthSp * emPerSp}em` : undefined,
          marginLeft: kind === 'inline' && spaces?.sl ? XSP : undefined,
          marginRight: kind === 'inline' && spaces?.sr ? XSP : undefined,
        }
      : { width: '1em', height: '1em' };
    // 未描画の間はスケルトン、リソース失敗時は失敗表示 (散文は残る)
    const state = degraded ? ' frag-formula-failed' : ' dvi-skeleton';
    return (
      <canvas
        className={`frag-formula frag-${kind}${state}`}
        style={style}
        title={degraded ? '数式を読み込めませんでした' : undefined}
        ref={(el) => {
          canvasRefs.current[page] = el;
        }}
      />
    );
  };

  // 本文を \par 区切りの段落にまとめる
  const paragraphs: React.ReactNode[][] = [[]];
  frag.body.forEach((seg, i) => {
    const para = paragraphs[paragraphs.length - 1];
    if (seg.t === 'br') {
      paragraphs.push([]);
    } else if (seg.t === 'text') {
      para.push(<span key={i}>{seg.s}</span>);
    } else if (seg.t === 'link') {
      para.push(
        seg.href.startsWith('/') ? (
          <Link key={i} to={seg.href}>
            {seg.s}
          </Link>
        ) : (
          <a key={i} href={seg.href} target="_blank" rel="noreferrer">
            {seg.s}
          </a>
        ),
      );
    } else if (seg.t === 'bib') {
      para.push(
        <span key={i}>
          <span className="frag-bib" id={`bib-${seg.key}`}>
            [{seg.label}]
          </span>{' '}
        </span>,
      );
    } else if (seg.t === 'cite') {
      para.push(<Cite key={i} seg={seg} />);
    } else if (seg.t === 'inline') {
      para.push(<span key={i}>{formulaCanvas(seg.p, 'inline', seg)}</span>);
    } else {
      para.push(
        <span key={i} className="frag-display-wrap">
          {formulaCanvas(seg.p, 'display')}
        </span>,
      );
    }
  });

  const draftClass = draft ? ' frag-draft' : '';
  // 対話図: 登録されたウィジェットを描画する (本文は DVI 等向けのフォールバック
  // 説明文なので Web では表示しない)。未登録名はフォールバック本文を出す
  if (frag.env === 'interactive') {
    const Widget = frag.label ? WIDGETS[frag.label] : undefined;
    if (Widget) {
      return (
        <div className={`frag frag-env-interactive${draftClass}`} ref={rootRef}>
          <Widget />
        </div>
      );
    }
  }
  // 見出し環境: 本文をそのまま h2/h3 相当の見出しとして組む
  if (frag.env === 'section' || frag.env === 'subsection') {
    const Tag = frag.env === 'section' ? 'h2' : 'h3';
    return (
      <div className={`frag frag-env-${frag.env}${draftClass}`} ref={rootRef}>
        <Tag className="frag-heading">{paragraphs.flat()}</Tag>
      </div>
    );
  }
  const title = ENV_TITLES[frag.env] + (frag.label ? ` ${frag.label}` : '');
  return (
    <div className={`frag frag-env-${frag.env}${draftClass}`} ref={rootRef}>
      {title !== '' && (
        <p className="frag-head">
          <b>{title}</b>
        </p>
      )}
      <div className="frag-body">
        {paragraphs.map((para, i) => (
          <div className="frag-para" key={i}>
            {para}
            {frag.qedPage !== null && i === paragraphs.length - 1 && (
              <span className="frag-qed">{formulaCanvas(frag.qedPage, 'qed')}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
